import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { createGlobalStyle } from "styled-components";

import { RootReducer } from "src/redux/reducers";
import { getUserInfoActions } from "src/redux/actions/user";
import { resetStateAction } from "src/redux/actions/auth";
import { signOutUser } from "src/services/localStorage";
import { StyledGriffleLayout } from "./GriffleLayout.styles";
import griffleMascot from "src/assets/images/griffle/griffledragon.svg";
import useUserType from "src/hooks/useUserType";
import NotificationCenter from "src/components/NotificationCenter/NotificationCenter";
import ClassSwitcher from "src/components/ClassSwitcher/ClassSwitcher";

/* Injected after all other styled-components so it always wins the !important battle */
const GriffleFontOverride = createGlobalStyle`
  .griffle-page input,
  .griffle-page input::placeholder,
  .griffle-page textarea,
  .griffle-page textarea::placeholder,
  .griffle-page select,
  .griffle-page button,
  .griffle-page .form-control,
  .griffle-page form .form-group .form-control,
  .griffle-page [contenteditable] {
    font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
  }
`;

interface GriffleLayoutProps {
  children: React.ReactNode;
}

const IconDashboard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1.5" fill="currentColor" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" fill="currentColor" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" fill="currentColor" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" fill="currentColor" />
  </svg>
);

const IconLibrary = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 19V6a2 2 0 012-2h12a2 2 0 012 2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M4 19a2 2 0 002 2h12a2 2 0 002-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M9 8h6M9 12h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconAssignments = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="3" width="14" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M9 7h6M9 11h6M9 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconProgress = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 20V14M9 20V9M14 20V12M19 20V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconTeacherResources = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3L2 8l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconSchools = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 21h18M6 21V10M18 21V10M3 10l9-7 9 7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect x="10" y="15" width="4" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const IconSignOut = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M10 17l5-5-5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 12H3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const GriffleLayout = ({ children }: GriffleLayoutProps) => {
  const [t] = useTranslation();
  const { userData } = useSelector((state: RootReducer) => state.user);
  const { authData } = useSelector((state: RootReducer) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUserType();

  const [progressOpen, setProgressOpen] = useState(false);

  const isProgressActive = location.pathname.startsWith("/griffle/progress-report");

  useEffect(() => {
    if (isProgressActive) setProgressOpen(true);
  }, [isProgressActive]);

  const handleProgressClick = () => {
    if (window.innerWidth <= 768) {
      navigate("/griffle/progress-report-detailed");
    } else {
      setProgressOpen((v) => !v);
    }
  };

  useEffect(() => {
    const userId = authData?.userId;
    if (userId && !userData) {
      dispatch(getUserInfoActions.request({ userId }));
    }
  }, [authData?.userId]); // intentional: userData/dispatch omitted to avoid refetch loops

  const handleLogout = () => {
    dispatch(resetStateAction);
    signOutUser();
    navigate("/griffle/login");
  };

  const firstName = userData?.firstName ?? userData?.name ?? "";

  return (
    <StyledGriffleLayout className="griffle-page">
      <GriffleFontOverride />
      {/* ── Header ──────────────────────────────────────────── */}
      <header className="griffle-header">
        <button className="header-brand" onClick={() => navigate("/griffle/dashboard")} aria-label="Go to dashboard">
          <span className="griffle-logo-text">GRIFFIN</span>
        </button>

        <div className="header-right">
          <ClassSwitcher />
          <NotificationCenter />

          <div className="header-user-pill" onClick={() => navigate("/griffle/dashboard")}>
            <div className="header-avatar">
              <img src={griffleMascot} alt="" aria-hidden="true" />
            </div>
            <span className="header-username">{firstName || "Admin"}</span>
            <span className="header-chevron">▾</span>
          </div>

          <button className="header-logout-btn" onClick={handleLogout}>
            {t("sidebar_button_logout_label")}
          </button>
        </div>
      </header>

      {/* ── Body ────────────────────────────────────────────── */}
      <div className="griffle-body">
        <aside className="griffle-sidebar" aria-label="Sidebar">
          <span className="sidebar-section-label">{t("sidebar_menu_label", "Menu")}</span>

          <nav className="sidebar-nav" aria-label="Main navigation">
            <NavLink
              to="/griffle/dashboard"
              className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}
            >
              <IconDashboard />
              <span className="nav-label">{t("sidebar_link_dashboard")}</span>
            </NavLink>

            {/* Progress Report — expandable submenu */}
            <div className="sidebar-nav-group">
              <button
                className={`sidebar-nav-group-trigger${progressOpen ? " open" : ""}${isProgressActive ? " open" : ""}`}
                onClick={handleProgressClick}
                aria-expanded={progressOpen || isProgressActive}
              >
                <IconProgress />
                <span className="nav-label">{t("sidebar_link_progress_report")}</span>
                <span className="nav-chevron">▾</span>
              </button>
              <div className={`sidebar-submenu${progressOpen || isProgressActive ? " open" : ""}`}>
                <NavLink
                  to="/griffle/progress-report-detailed"
                  className={({ isActive }) => `sidebar-submenu-item${isActive ? " active" : ""}`}
                >
                  {t("sidebar_link_progress_detailed")}
                </NavLink>
                <NavLink
                  to="/griffle/progress-report-class"
                  className={({ isActive }) => `sidebar-submenu-item${isActive ? " active" : ""}`}
                >
                  {t("sidebar_link_progress_class")}
                </NavLink>
                <NavLink
                  to="/griffle/progress-report-students"
                  className={({ isActive }) => `sidebar-submenu-item${isActive ? " active" : ""}`}
                >
                  {t("sidebar_link_progress_students")}
                </NavLink>
              </div>
            </div>

            <NavLink to="/griffle/library" className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}>
              <IconLibrary />
              <span className="nav-label">{t("sidebar_link_book_library")}</span>
            </NavLink>

            <NavLink
              to="/griffle/teacher-resources"
              className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}
            >
              <IconTeacherResources />
              <span className="nav-label">{t("sidebar_link_teacher_resources")}</span>
            </NavLink>

            <NavLink
              to="/griffle/assignments"
              className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}
            >
              <IconAssignments />
              <span className="nav-label">{t("sidebar_link_assignments", "Assignments")}</span>
            </NavLink>

            {isAdmin && (
              <NavLink
                to="/griffle/schools"
                className={({ isActive }) => `sidebar-nav-item${isActive ? " active" : ""}`}
              >
                <IconSchools />
                <span className="nav-label">{t("sidebar_link_schools", "Schools")}</span>
              </NavLink>
            )}
          </nav>

          <div className="sidebar-divider" />

          <div className="sidebar-footer">
            <button
              className="sidebar-nav-item"
              onClick={handleLogout}
              style={{ width: "100%", background: "none", border: "none", cursor: "pointer" }}
            >
              <IconSignOut />
              <span className="nav-label">{t("sidebar_button_logout_label")}</span>
            </button>
          </div>
        </aside>

        <main className="griffle-content">
          <div className="griffle-page">{children}</div>
        </main>
      </div>
    </StyledGriffleLayout>
  );
};

export default GriffleLayout;
